import { BillingDocumentException } from "constants/billingDocuments";
import {
  AbnStatus,
  BUYER,
  GstStatus,
  TRANSPORTER,
  VENDOR,
} from "constants/businesses";

/**
 * Compares the Deployment Business name to the given name.
 * Note: This does not take in to account Deployments/Agency linkages
 *
 * @param {string} businessName A Deployment Business Name
 * @returns {function(*): boolean}
 */
export function findBusinessWithName(businessName) {
  return business => business.name === businessName;
}

export const setBusinessRoleAndInsuranceFlag = (
  business,
  roles,
  value = true,
) => {
  // Set a businesses role, and other relevant fields.
  if (Array.isArray(roles) && roles.length === 1) {
    const role = roles[0];
    if (role === VENDOR) {
      business.is_vendor = value;
      business.has_transit_insurance_for_sales = value;
    } else if (role === TRANSPORTER) {
      business.is_transporter = value;
    } else if (role === BUYER) {
      business.is_buyer = value;
    }
  }
};

export function getBusinessSubType(business) {
  if (business.businessLivestockAgentId) {
    return "Livestock Agent";
  } else if (business.businessDeploymentId) {
    return "Livestock Agency";
  }
  return "Business";
}

export const getGSTStatusText = (
  isABNActive,
  isGstRegistered,
  abrLastChecked,
  shouldChargeGSTOverride = null,
) => {
  const result = [];
  if (abrLastChecked) {
    if (isABNActive === false) {
      result.push(GstStatus.NOT_REGISTERED_ABN_CANCELLED);
    } else if (isABNActive === true && isGstRegistered === true) {
      result.push(GstStatus.REGISTERED);
    } else if (isABNActive === null && isGstRegistered === true) {
      result.push(GstStatus.REGISTERED_NO_ABN_STATUS);
    } else {
      result.push(GstStatus.NOT_REGISTERED);
    }
  } else {
    result.push(GstStatus.UNKNOWN);
  }
  if (shouldChargeGSTOverride !== null) {
    if (shouldChargeGSTOverride) {
      result.push(`(${GstStatus.OVERRIDDEN_TO_CHARGE_GST})`);
    } else {
      result.push(`(${GstStatus.OVERRIDDEN_TO_NO_GST})`);
    }
  }
  return result.join(" ");
};

export const getAbnStatusText = business => {
  const hasAbn = Boolean(business.abn);
  const { abrLastChecked } = business;
  const { isABNActive } = business;

  if (hasAbn) {
    if (!abrLastChecked) {
      return AbnStatus.ABR_NOT_QUERIED;
    } else if (isABNActive === true) {
      return AbnStatus.ACTIVE;
    } else if (isABNActive === false) {
      return AbnStatus.CANCELLED;
    } else {
      return AbnStatus.STATUS_NOT_QUERIED;
    }
  } else {
    return AbnStatus.NO_ABN;
  }
};

export const getBuyerInformationString = (
  buyerId,
  destinationPropertyId,
  buyerWayName,
) => [buyerId, destinationPropertyId, buyerWayName].join("__");

// Inverse of getBuyerInformationString. Returns [buyerId, destinationPropertyId,
// buyerWayName], with empty-string parts converted back to null.
export const parseBuyerInformationString = buyerInformationString => {
  const [buyerIdStr, destinationPropertyIdStr, buyerWayNameStr] =
    buyerInformationString.split("__");
  return [
    buyerIdStr || null,
    destinationPropertyIdStr || null,
    buyerWayNameStr || null,
  ];
};

// A business is considered active when it's:
// - got the isActive flag Tue
// AND
// - is not temporary OR is temporary and added in the current sale  (ie - they're only useful for a selected sale
// and should not be shown outside that context)
export const isBusinessActive = (business, currentLivestockSaleId) => {
  return (
    business.isActive &&
    (!business.isTemporary ||
      business.addedInLivestockSaleId === currentLivestockSaleId)
  );
};

export const getPrimaryContactFromBusiness = business => {
  const { primaryContactEmail, emailRecipients, businessUsers } = business;
  const emailRecipientWithEmail = emailRecipients.filter(er => er.email);
  if (primaryContactEmail) {
    const primaryEmailRecipient = emailRecipientWithEmail.find(
      er => er.email === primaryContactEmail,
    );
    const primaryBusinessUser = businessUsers.find(
      bu => bu.email === primaryContactEmail,
    );
    return primaryEmailRecipient || primaryBusinessUser;
  } else {
    if (emailRecipientWithEmail?.length) {
      return emailRecipientWithEmail[0];
    } else if (businessUsers?.length) {
      return businessUsers[0];
    }
    return null;
  }
};

export const getAbnWarningsListFromBusiness = business => {
  const warningsList = [];

  const {
    abn,
    isHobbyFarmer,
    shouldChargeGSTOverride,
    isGSTRegistered,
    isABNActive,
  } = business;

  if (!isHobbyFarmer) {
    if (abn) {
      if (isABNActive === false) {
        warningsList.push(BillingDocumentException.VENDOR_CANCELLED_ABN);
      } else if (!isGSTRegistered) {
        warningsList.push(BillingDocumentException.VENDOR_NOT_GST_REGISTERED);
      }
    } else {
      warningsList.push(BillingDocumentException.VENDOR_MISSING_ABN);
    }

    // need to explicity check true vs false on override as null means no override has been applied
    if (shouldChargeGSTOverride === true) {
      warningsList.push(BillingDocumentException.VENDOR_GST_OVERRIDDEN_GST);
    }
    if (shouldChargeGSTOverride === false) {
      warningsList.push(BillingDocumentException.VENDOR_GST_OVERRIDDEN_NO_GST);
    }
  }
  return warningsList;
};
